import { SupabaseClient } from "@supabase/supabase-js";

import { Database } from "../../supabase.types";
import { logException, logSupabaseException } from "@punchup/sentry";
import type {
  ReportEventLineup,
  ReportPerformer,
} from "../../models/report-lineups";
import { getPunchupShowIdsByTixologiIds } from "../public/shows";

/** Every column the wizard's artist picker reads, joined off the pairing. */
const PAIRED_COMEDIAN_SELECT = `show_id,
      ordering,
      comedian:comedians!show_comedian_pairings_comedian_id_fkey(id, display_name, display_name_short, about_img)`;

/**
 * Resolve WHO each of these ticketing events had on stage, by way of the Punchup
 * show it is linked to — the acts the wizard's artist picker offers.
 *
 * Null for every case that yields nobody, so a caller offers no act to pick
 * rather than an empty list to puzzle over.
 */
export async function getReportPerformersForTixologiEvents(
  supabase: SupabaseClient<Database>,
  tixologiEventIds: number[]
): Promise<ReportEventLineup[] | null> {
  if (tixologiEventIds.length === 0) return null;

  try {
    const showIdByEventId = await getPunchupShowIdsByTixologiIds(
      tixologiEventIds.map((id) => String(id)),
      supabase
    );

    const performersByShowId = await performersByShowIdFor(
      supabase,
      Object.values(showIdByEventId)
    );
    if (!performersByShowId) return null;

    const lineups = tixologiEventIds.flatMap((eventId) => {
      const showId = showIdByEventId[String(eventId)];
      const performers = showId ? (performersByShowId[showId] ?? []) : [];
      return performers.length > 0 ? [{ event_id: eventId, performers }] : [];
    });

    return lineups.length > 0 ? lineups : null;
  } catch (error) {
    logException(error, {
      data: "[report-lineups] the show register read failed — the artist picker offers no act from the bills",
      extra: { eventCount: tixologiEventIds.length },
    });
    return null;
  }
}

/** Every paired comedian per show, in the venue's own billing order, or null
 *  when the pairings cannot be read. A pairing whose comedian has no display
 *  name is skipped: an unnamed act cannot be picked. */
async function performersByShowIdFor(
  supabase: SupabaseClient<Database>,
  showIds: string[]
): Promise<Record<string, ReportPerformer[]> | null> {
  if (showIds.length === 0) return {};

  const { data, error } = await supabase
    .from("show_comedian_pairings")
    .select(PAIRED_COMEDIAN_SELECT)
    .in("show_id", showIds)
    .order("ordering", { ascending: true });

  if (error) {
    logSupabaseException(
      error,
      "[report-lineups] show_comedian_pairings read failed — the artist picker offers no act from the bills",
      { extra: { showCount: showIds.length } }
    );
    return null;
  }

  const performersByShowId: Record<string, ReportPerformer[]> = {};
  for (const pairing of data ?? []) {
    const performer = namedPerformer(pairing.comedian);
    if (!performer) continue;
    (performersByShowId[pairing.show_id] ??= []).push(performer);
  }
  return performersByShowId;
}

/** The picker's four columns off a joined comedian row, or null when the row
 *  names nobody. */
function namedPerformer(
  comedian: {
    id?: string | null;
    display_name?: string | null;
    display_name_short?: string | null;
    about_img?: string | null;
  } | null
): ReportPerformer | null {
  const displayName = comedian?.display_name?.trim();
  if (!comedian?.id || !displayName) return null;
  return {
    id: comedian.id,
    display_name: displayName,
    display_name_short: comedian.display_name_short?.trim() || displayName,
    about_img: comedian.about_img ?? null,
  };
}
